import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/admin/AdminCompanyDetail.css';
import Dialog from '../../components/Dialog';
import { getMiddleUserByCompanyCode, getCompanyPlanInfo, updateMiddleUserCompany, updateMiddleUserCompanyByBusinessNumber, deleteCompany } from '../../utils/api';

const AdminCompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    representative: '',
    phone: '',
    businessNumber: ''
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // API로 회사 정보 가져오기
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        
        // URL 파라미터에서 회사코드와 사업자번호 가져오기 
        // 페이지 새로고침 시 정보가 없을 수 있으므로 로컬스토리지에 저장해둔 값이 있는지 확인
        const companyCode = localStorage.getItem(`company_${id}_code`);
        const businessNumber = localStorage.getItem(`company_${id}_business_number`);
        
        if (!companyCode && !businessNumber) {
          console.warn('회사 코드와 사업자번호를 찾을 수 없습니다. 리스트 페이지로 이동합니다.');
          alert('회사 정보를 찾을 수 없습니다.');
          navigate('/admin/company-list');
          return;
        }
        
        // 회사 코드 또는 사업자번호로 중간관리자 정보 조회
        console.log(`회사 정보 조회 중... 회사코드: ${companyCode || '없음'}, 사업자번호: ${businessNumber || '없음'}`);
        const response = await getMiddleUserByCompanyCode(companyCode, businessNumber);
        console.log('회사 정보 응답:', response);
        
        // 플랜 정보 가져오기
        let planInfo = {
          expiryDate: '정보 없음',
          remainingDays: '정보 없음'
        };
        
        try {
          // 회사 코드로 플랜 정보 조회 (회사 코드가 있는 경우에만)
          if (companyCode) {
            const planResponse = await getCompanyPlanInfo(companyCode);
            console.log('플랜 정보 응답:', planResponse);
            
            if (planResponse && planResponse.status === 'success' && planResponse.data) {
              // 날짜 형식 변환 (2025-11-25T23:50:33.858000 -> 2025-11-25)
              const endDate = planResponse.data.end_date 
                ? planResponse.data.end_date.split('T')[0]
                : null;
              
              const startDate = planResponse.data.start_date
                ? planResponse.data.start_date.split('T')[0]
                : null;
              
              // 남은 일수 계산
              let remainingDays = '정보 없음';
              if (endDate) {
                const today = new Date();
                const expiry = new Date(endDate);
                const diffTime = expiry.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                remainingDays = diffDays > 0 
                  ? `${diffDays}일` 
                  : diffDays === 0 
                    ? '오늘 만료' 
                    : '만료됨';
              }
              
              planInfo = {
                expiryDate: endDate || '정보 없음',
                remainingDays
              };
            }
          }
        } catch (planError) {
          console.error('플랜 정보 로딩 오류:', planError);
          // 플랜 정보 로드 실패 시 기본값 사용 (이미 설정됨)
        }
        
        if (response && response.middle_users && response.middle_users.length > 0) {
          // 회사 정보 찾기
          let matchedUser;
          
          if (companyCode && companyCode.trim() !== '') {
            // 회사 코드가 있으면 회사 코드로 정확히 매칭
            matchedUser = response.middle_users.find(
              user => user.middleCompanyCode === companyCode
            );
          } else if (businessNumber && businessNumber.trim() !== '') {
            // 회사 코드가 없고 사업자번호가 있으면 사업자번호로 매칭
            matchedUser = response.middle_users.find(
              user => user.middleBusinessNumber === businessNumber
            );
            
            // 정확히 매칭되는 회사가 없으면 첫 번째 결과 사용
            if (!matchedUser && response.middle_users.length > 0) {
              matchedUser = response.middle_users[0];
            }
          }
          
          if (matchedUser) {
            console.log('일치하는 중간관리자 세부 정보:', matchedUser);
            
            // 날짜 형식 변환 (2025-04-22T16:05:05.456000 -> 2025-04-22)
            const registrationDate = matchedUser.created_at 
              ? matchedUser.created_at.split('T')[0] 
              : '정보 없음';
            
            // 회사 정보 설정
            const companyData = {
              id: id,
              name: matchedUser.middleCompanyName || '이름 없음',
              representative: matchedUser.middleName || '정보 없음',
              phone: matchedUser.middlePhone || '정보 없음',
              businessNumber: matchedUser.middleBusinessNumber || '정보 없음',
              companyCode: matchedUser.middleCompanyCode || '정보 없음',
              registrationDate: registrationDate,
              expiryDate: planInfo.expiryDate,
              remainingDays: planInfo.remainingDays
            };
            
            // 상태 업데이트
            setCompany(companyData);
            setEditData({
              representative: companyData.representative,
              phone: companyData.phone,
              businessNumber: companyData.businessNumber
            });
          } else {
            console.error('해당 정보와 일치하는 중간관리자 정보가 없습니다.');
            throw new Error('회사 정보를 찾을 수 없습니다.');
          }
        } else {
          console.error('중간관리자 정보를 찾을 수 없습니다.');
          throw new Error('회사 정보를 가져오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('회사 정보 로딩 오류:', error);
        // 오류 발생 시 더미 데이터로 대체 (개발 목적으로만 사용)
        const dummyData = {
          id: id,
          name: '회사명 로드 실패',
          representative: '정보 없음',
          phone: '정보 없음',
          businessNumber: '정보 없음',
          companyCode: '정보 없음',
          registrationDate: '정보 없음',
          expiryDate: '정보 없음',
          remainingDays: '정보 없음'
        };
        
        setCompany(dummyData);
        setEditData({
          representative: dummyData.representative,
          phone: dummyData.phone,
          businessNumber: dummyData.businessNumber
        });
        
        // 오류 메시지 표시 (옵션)
        // alert('회사 정보를 불러오는 중 오류가 발생했습니다: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanyData();
  }, [id, navigate]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      setIsSubmitting(true);
      
      // 수정할 데이터 준비
      const updateData = {
        middleName: editData.representative,
        middlePhone: editData.phone,
        middleBusinessNumber: editData.businessNumber
      };
      
      console.log('회사 정보 수정 요청 데이터:', updateData);
      
      let response;
      
      // 회사 코드가 있으면 회사 코드로 수정, 없으면 사업자번호로 수정
      if (company.companyCode && company.companyCode !== '정보 없음') {
        // 회사 코드로 API 호출하여 회사 정보 수정
        console.log('회사 코드로 정보 수정:', company.companyCode);
        response = await updateMiddleUserCompany(company.companyCode, updateData);
      } else if (company.businessNumber && company.businessNumber !== '정보 없음') {
        // 사업자번호로 API 호출하여 회사 정보 수정
        console.log('사업자번호로 정보 수정:', company.businessNumber);
        response = await updateMiddleUserCompanyByBusinessNumber(company.businessNumber, updateData);
      } else {
        throw new Error('회사 코드 또는 사업자번호가 유효하지 않습니다.');
      }
      
      console.log('회사 정보 수정 성공:', response);
      
      // 성공적으로 수정된 경우 컴포넌트 상태 업데이트
      setCompany({
        ...company,
        representative: editData.representative,
        phone: editData.phone,
        businessNumber: editData.businessNumber
      });
      
      // 성공 다이얼로그 표시
      setDialogType('company-edit-success');
      setDialogOpen(true);
    } catch (error) {
      console.error('회사 정보 수정 오류:', error);
      
      // 오류 다이얼로그 표시
      setDialogType('company-edit-error');
      setDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    // 삭제 확인 다이얼로그 표시
    setDialogType('company-delete-confirm');
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      
      // 회사 코드와 사업자번호 가져오기
      const companyCode = company.companyCode !== '정보 없음' ? company.companyCode : null;
      const businessNumber = company.businessNumber !== '정보 없음' ? company.businessNumber : null;
      
      console.log('회사 삭제 요청 중...', { companyCode, businessNumber });
      
      // API 호출 - company_code를 우선 사용하고, 없으면 business_number 사용
      const response = await deleteCompany(companyCode, businessNumber);
      console.log('회사 삭제 응답:', response);
      
      // 다이얼로그 닫기
      setDialogOpen(false);
      
      // 성공 다이얼로그 표시
      setTimeout(() => {
        setDialogType('company-delete-success');
        setDialogOpen(true);
      }, 300);
      
    } catch (error) {
      console.error('회사 삭제 오류:', error);
      
      // 다이얼로그 닫기
      setDialogOpen(false);
      
      // 오류 다이얼로그 표시
      setTimeout(() => {
        setDialogType('company-delete-error');
        setDialogOpen(true);
      }, 300);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value
    });
  };

  if (loading) return <div className="loading">로딩 중...</div>;
  if (!company) return <div className="errorMessage">업체 정보를 찾을 수 없습니다.</div>;

  return (
    <div className="clean-container">
      <div className="header-section">
        <h1 className="company-title">{company.name}</h1>
        {!isEditing ? (
          <button className="edit-button" onClick={handleEditClick}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.474 5.408L18.592 7.526L8.618 17.5L6.5 17.5L6.5 15.382L16.474 5.408ZM16.474 3.994L5.086 15.382L5.086 19.5L9.204 19.5L20.592 8.112L16.474 3.994Z" fill="black"/>
            </svg>
            <span className="button-text">업체 정보 수정</span>
          </button>
        ) : (
          <div className="action-buttons">
            <button className="delete-button" onClick={handleDeleteClick}>
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 7H18M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7M10 11V17M14 11V17M4 7H20L19 21H5L4 7Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span className="button-text">업체 삭제</span>
            </button>
            <button 
              className="save-button" 
              onClick={handleSaveClick}
              disabled={isSubmitting}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12L10 17L20 7" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span className="button-text">{isSubmitting ? '저장 중...' : '수정 완료'}</span>
            </button>
          </div>
        )}
      </div>
      
      <div className="clean-divider"></div>
      
      <div className="content-section">
        <h2 className="section-title">업체 정보</h2>
        
        <div className="info-list">
          <div className="info-row">
            <div className="info-label">대표명</div>
            {!isEditing ? (
              <div className="info-value">{company.representative}</div>
            ) : (
              <input
                type="text"
                name="representative"
                value={editData.representative}
                onChange={handleInputChange}
                className="edit-input"
              />
            )}
          </div>
          
          <div className="info-row">
            <div className="info-label">전화번호</div>
            {!isEditing ? (
              <div className="info-value">{company.phone}</div>
            ) : (
              <input
                type="text"
                name="phone"
                value={editData.phone}
                onChange={handleInputChange}
                className="edit-input"
              />
            )}
          </div>
          
          <div className="info-row">
            <div className="info-label">사업자번호</div>
            {!isEditing ? (
              <div className="info-value">{company.businessNumber}</div>
            ) : (
              <input
                type="text"
                name="businessNumber"
                value={editData.businessNumber}
                onChange={handleInputChange}
                className="edit-input"
              />
            )}
          </div>
          
          <div className="info-row">
            <div className="info-label">회사코드</div>
            <div className="info-value">{company.companyCode}</div>
          </div>
          
          <div className="info-row">
            <div className="info-label">등록일</div>
            <div className="info-value">{company.registrationDate}</div>
          </div>
        </div>
      </div>
      
      <div className="clean-divider"></div>
      
      <div className="content-section">
        <h2 className="section-title">결제 정보</h2>
        
        <div className="info-list">
          <div className="info-row">
            <div className="info-label">플랜 만료일</div>
            <div className="info-value">{company.expiryDate}</div>
          </div>
          
          <div className="info-row">
            <div className="info-label">남은 기간</div>
            <div className="info-value">{company.remainingDays}</div>
          </div>
        </div>
      </div>

      <Dialog 
        isOpen={dialogOpen} 
        type={dialogType} 
        onClose={handleDialogClose} 
        onConfirm={(e) => {
          // If it's the delete confirmation dialog, handle the deletion process
          if (dialogType === 'company-delete-confirm') {
            handleDeleteConfirm();
          } 
          // If it's the deletion success dialog, navigate back to the list
          else if (dialogType === 'company-delete-success') {
            navigate('/admin/company-list');
          }
          // For edit success dialog, exit edit mode
          else if (dialogType === 'company-edit-success') {
            handleDialogClose();
            setIsEditing(false);
          }
          // For other dialogs, just close them
          else {
            handleDialogClose();
          }
        }}
        onCancel={handleDialogCancel}
      />
    </div>
  );
};

export default AdminCompanyDetail; 